/**
 * TemplateEditor Component
 *
 * Jodit-based WYSIWYG editor for email template HTML content.
 * Preserves full HTML structure including styles, unlike Tiptap.
 * Save/Cancel buttons integrated into toolbar via extraButtons.
 */

import { useRef, useCallback, useMemo, useEffect, useState } from 'react'
import JoditEditor from 'jodit-react'
import { Jodit } from 'jodit'
import { registerLucideIcons } from './joditIcons'
import './TemplateEditor.css'

// Register Lucide icons with Jodit (only once)
let iconsRegistered = false
if (!iconsRegistered && typeof window !== 'undefined') {
  registerLucideIcons(Jodit)
  iconsRegistered = true
}

interface TemplateEditorProps {
  initialContent: string
  onSave: (htmlContent: string) => void
  onCancel: () => void
  isSaving?: boolean
  isAiProcessing?: boolean
  pendingAiHtml?: string | null
  onAiApplied?: (appliedHtml: string) => void
  originalContent?: string
  onRevert?: () => void
}

export function TemplateEditor({
  initialContent,
  onSave,
  onCancel,
  isSaving = false,
  isAiProcessing = false,
  pendingAiHtml = null,
  onAiApplied,
  originalContent: _originalContent, // Used by parent via onRevert callback
  onRevert,
}: TemplateEditorProps) {
  const editorRef = useRef<any>(null)
  const contentRef = useRef(initialContent)
  const onSaveRef = useRef(onSave)
  const onCancelRef = useRef(onCancel)
  const onRevertRef = useRef(onRevert)
  const [aiPulse, setAiPulse] = useState(false)

  // Keep refs updated
  useEffect(() => {
    onSaveRef.current = onSave
    onCancelRef.current = onCancel
    onRevertRef.current = onRevert
  }, [onSave, onCancel, onRevert])

  // Apply AI changes when pendingAiHtml changes
  useEffect(() => {
    if (!pendingAiHtml) return

    // Validate HTML (must contain basic structure)
    const isValid = pendingAiHtml.includes('<')
    if (!isValid) {
      console.warn('[TemplateEditor] Invalid HTML received:', pendingAiHtml.substring(0, 100))
      return
    }

    const applyHtml = () => {
      const editor = editorRef.current
      if (!editor) {
        console.warn('[TemplateEditor] Editor ref not available')
        return
      }

      // Use setEditorValue if available (preferred), fallback to value setter
      if (typeof editor.setEditorValue === 'function') {
        editor.setEditorValue(pendingAiHtml)
      } else {
        editor.value = pendingAiHtml
      }
      contentRef.current = pendingAiHtml

      // Trigger success pulse animation
      setAiPulse(true)
      setTimeout(() => setAiPulse(false), 600)

      // Notify parent with the applied HTML
      onAiApplied?.(pendingAiHtml)
    }

    // Use requestAnimationFrame to ensure DOM is ready (helps with iframe mode)
    requestAnimationFrame(applyHtml)
  }, [pendingAiHtml, onAiApplied])

  // Set editor readonly state based on AI processing
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.setReadOnly(isAiProcessing)
    }
  }, [isAiProcessing])


  // Jodit configuration - fullpage mode preserves complete HTML
  const config = useMemo(() => ({
    readonly: false,
    height: '100%',
    minHeight: 500,
    toolbarAdaptive: false,
    toolbarSticky: true,
    toolbarStickyOffset: 0,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: true,
    askBeforePasteFromWord: false,
    // Enable full page editing (preserves <html>, <head>, <style>, etc.)
    iframe: true,
    iframeStyle: [
      'html, body, p, td, th, li, div, span { font-family: Arial, Helvetica, sans-serif; }',
      'body { margin: 0; padding: 8px; }',
      // Dashed borders on tables during editing so cells are visible and selectable.
      // Uses !important because email templates have their own <style> that may set
      // border:none on cells — without !important those styles override ours.
      // These styles only apply inside the editor iframe, not the final email output.
      'table { border: 1px dashed #ccc !important; border-collapse: collapse !important; }',
      'td, th { border: 1px dashed #ddd !important; padding: 4px !important; min-height: 24px !important; }',
    ].join(' '),
    // Use WYSIWYG by default, source view available via toolbar
    defaultMode: 1, // 1 = WYSIWYG, 2 = Source, 3 = Split
    // Toolbar buttons with custom save/cancel at end
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'link', 'table', '|',
      'align', '|',
      'undo', 'redo', '|',
      'hr', 'eraser', 'fullsize',
    ],
    // Extra buttons - Revert, Cancel, and Save in toolbar
    extraButtons: [
      {
        name: 'revertTemplate',
        icon: 'undo',
        tooltip: 'Revert to original',
        exec: () => {
          if (confirm('Revert to original? All changes will be lost.')) {
            onRevertRef.current?.()
          }
        }
      },
      {
        name: 'cancelTemplate',
        icon: 'cancel',
        tooltip: 'Cancel editing',
        exec: () => {
          onCancelRef.current()
        }
      },
      {
        name: 'saveTemplate',
        icon: 'save',
        tooltip: 'Save template',
        exec: () => {
          onSaveRef.current(contentRef.current)
        }
      }
    ],
    // Colors matching Eventus brand
    colors: {
      greyscale: ['#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF'],
      palette: ['#053252', '#877be9', '#91d9e8', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
    },
    // Disable features that interfere with email HTML
    cleanHTML: {
      fillEmptyParagraph: false,
      removeEmptyElements: false,
      replaceOldTags: false as const,
      removeEmptyNodes: false,
    },
    // Prevent Jodit from stripping/mangling inline styles.
    // allowTabNavigation must be false: when true, Jodit appends dropdown popups
    // as children of their trigger button (for keyboard accessibility). But
    // .jodit-popup has transform:translate3d(0,0,0) which creates a new CSS
    // containing block — making nested position:fixed popups relative to the
    // parent popup instead of the viewport, causing ~400px offset.
    allowTabNavigation: false,
    beautifyHTML: false,
    sourceEditorNativeOptions: {
      theme: 'ace/theme/idle_fingers',
    },
    // Preserve all HTML elements and attributes for email templates
    allowResizeY: false,
    allowResizeX: false,
    // Disable enter mode conversion that can strip styles
    enter: 'br' as const,
    // Keep inline styles intact
    disablePlugins: ['clean-html.replaceOldTags'],
    // Default font for new content
    defaultFontSizePoints: 'pt' as const,
    style: {
      font: '14px Arial, Helvetica, sans-serif',
    },
    // Theme
    theme: 'default',
    // Status bar disabled to save space
    statusbar: false,
  }), [])

  // Track content changes
  const handleChange = useCallback((newContent: string) => {
    contentRef.current = newContent
  }, [])

  return (
    <div className={`template-editor jodit-editor-wrapper h-full flex flex-col ${isAiProcessing ? 'ai-processing' : ''} ${aiPulse ? 'ai-success-pulse' : ''}`}>
      <div className="flex-1 min-h-0">
        <JoditEditor
          ref={editorRef}
          value={initialContent}
          config={config}
          onBlur={handleChange}
          onChange={handleChange}
        />
      </div>

      {/* AI Processing overlay */}
      {isAiProcessing && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50">
          <div className="flex items-center gap-3 px-4 py-2 bg-eventus-purple text-white rounded-lg shadow-lg">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span className="font-medium">AI editing...</span>
          </div>
        </div>
      )}

      {/* Saving overlay */}
      {isSaving && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50">
          <div className="flex items-center gap-3 px-4 py-2 bg-eventus-purple text-white rounded-lg shadow-lg">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span className="font-medium">Saving...</span>
          </div>
        </div>
      )}
    </div>
  )
}
